/**
 * unsplashService.ts - Unsplash API Integration Service
 *
 * This service provides integration with Unsplash API for searching and
 * retrieving high-quality images for fundraiser campaigns.
 *
 * Key Features:
 * - Search images by keywords and categories
 * - Filter by orientation, size, and quality
 * - Get random images for specific topics
 * - Download tracking and attribution
 * - Error handling and rate limiting
 *
 * API Documentation: https://unsplash.com/documentation
 *
 * Dependencies: Unsplash API, fetch API
 */

import { ApplicationSettingsStore, OpenAISettingsDataModel } from "@mindmemobile/mm-payment-modules";
import { OpenAIService } from "./openaiService";

interface UnsplashImage {
  id: string;
  urls: {
    raw: string;
    full: string;
    regular: string;
    small: string;
    thumb: string;
  };
  alt_description: string | null;
  description: string | null;
  user: {
    name: string;
    username: string;
  };
  links: {
    html: string;
    download: string;
    download_location: string;
  };
  width: number;
  height: number;
}

interface UnsplashSearchResponse {
  total: number;
  total_pages: number;
  results: UnsplashImage[];
}

interface UnsplashResponse {
  success: boolean;
  data?: UnsplashImage | UnsplashImage[];
  error?: string;
}

export class UnsplashService1 {
  /* The access key comes from the Payment API's ApplicationSettings (UnsplashAccessKey). It and the
     app's secret key used to be literals here, shipped in the browser bundle (security finding F03).
     The secret key is never needed for Client-ID requests, so it is gone from the client entirely. */
  private static get ACCESS_KEY(): string {
    return ApplicationSettingsStore.getUnsplashAccessKey();
  }
  private static readonly BASE_URL = "https://api.unsplash.com";

  /**
   * Test Unsplash API connection
   */
  static async testConnection(): Promise<UnsplashResponse> {
    try {
      console.log("🧪 Testing Unsplash API connection...");
      const response = await fetch(
        `${this.BASE_URL}/search/photos?query=soccer&per_page=1`,
        {
          headers: {
            Authorization: `Client-ID ${this.ACCESS_KEY}`,
            "Accept-Version": "v1",
          },
        }
      );

      console.log("🧪 Test response status:", response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("🧪 Test failed:", errorText);
        return { success: false, error: `API test failed: ${response.status}` };
      }

      const data = await response.json();
      console.log("🧪 Test successful, found", data.total, "images");
      return { success: true, data: data.results?.[0] || null };
    } catch (error) {
      console.error("🧪 Test error:", error);
      return { success: false, error: "Connection test failed" };
    }
  }

  /**
   * Search for images based on fundraiser description and title
   */
  static async searchFundraiserImage(
    description: string,
    title: string,
    category?: string
  ): Promise<UnsplashResponse> {
    if (!this.ACCESS_KEY) {
      return { success: false, error: "Unsplash access key not configured" };
    }
    const openAIService = new OpenAIService(ApplicationSettingsStore.getOpenAISettings() || new OpenAISettingsDataModel());
    try {
      console.log("🔍 Searching Unsplash for fundraiser image...", {
        description,
        title,
        category,
      });

      // Try to generate AI-powered search terms first
      let searchQuery = "";
      try {
        if (openAIService.hasValidApiKey()) {
          const searchTermsResult =
            await openAIService.generateImageSearchTerms(
              description,
              title,
              category || ""
            );
          if (searchTermsResult.success) {
            searchQuery = searchTermsResult.data;
            console.log("🤖 AI-generated search query:", searchQuery);
          }
        }
      } catch (aiError) {
        console.log("⚠️ AI search terms failed, using fallback");
      }

      // Fallback to manual search query if AI fails
      if (!searchQuery) {
        searchQuery = this.generateSearchQuery(description, title, category);
        console.log("🔎 Fallback search query:", searchQuery);
      }

      const url = `${this.BASE_URL}/search/photos?query=${encodeURIComponent(searchQuery)}&per_page=10&orientation=landscape&order_by=relevant`;
      console.log("🌐 Unsplash API request:", url);

      const response = await fetch(url, {
        headers: {
          Authorization: `Client-ID ${this.ACCESS_KEY}`,
          "Accept-Version": "v1",
        },
      });

      console.log("📡 Unsplash response status:", response.status);
      // console.log(
      //   "📡 Unsplash response headers:",
      //   Object.fromEntries(response.headers.entries())
      // );

      if (!response.ok) {
        const errorText = await response.text();
        console.error("❌ Unsplash API error:", response.status, errorText);
        throw new Error(
          `Unsplash API error: ${response.status} - ${errorText}`
        );
      }

      const data: UnsplashSearchResponse = await response.json();
      console.log("📊 Unsplash search results:", data.total, "images found");

      if (!data.results || data.results.length === 0) {
        console.log("🔄 No results found, trying fallback search...");
        return await this.fallbackSearch(category);
      }

      // Select the best image from results
      const selectedImage = this.selectBestImage(data.results);
      console.log(
        "✅ Selected image:",
        selectedImage.id,
        selectedImage.alt_description
      );

      // Track download for attribution
      await this.trackDownload(selectedImage.links.download_location);

      return { success: true, data: selectedImage };
    } catch (error) {
      console.error("💥 Error searching Unsplash:", error);
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Failed to search images",
      };
    }
  }

  /**
   * Generate search query based on fundraiser content
   */
  private static generateSearchQuery(
    description: string,
    title: string,
    category?: string
  ): string {
    const keywords: string[] = [];

    // Add category-specific keywords
    if (category) {
      const categoryKeywords = this.getCategoryKeywords(category);
      keywords.push(...categoryKeywords);
    }

    // Extract keywords from title and description
    const titleKeywords = this.extractKeywords(title);
    const descriptionKeywords = this.extractKeywords(description);

    keywords.push(...titleKeywords.slice(0, 2)); // Top 2 from title
    keywords.push(...descriptionKeywords.slice(0, 2)); // Top 2 from description

    // Add general fundraising terms
    keywords.push("community", "support", "help");

    // Remove duplicates and join
    const uniqueKeywords = Array.from(new Set(keywords));
    return uniqueKeywords.slice(0, 5).join(" "); // Max 5 keywords
  }

  /**
   * Get category-specific keywords for better search results
   */
  private static getCategoryKeywords(category: string): string[] {
    const categoryMap: { [key: string]: string[] } = {
      "Medical & Health": [
        "medical",
        "healthcare",
        "hospital",
        "doctor",
        "health",
      ],
      "Education & Schools": [
        "education",
        "school",
        "students",
        "learning",
        "classroom",
      ],
      "Animals & Pets": [
        "animals",
        "pets",
        "dogs",
        "cats",
        "wildlife",
        "rescue",
      ],
      "Sports & Teams": [
        "sports",
        "team",
        "athletes",
        "competition",
        "training",
      ],
      "Community Projects": [
        "community",
        "neighborhood",
        "local",
        "together",
        "volunteer",
      ],
      "Environment & Conservation": [
        "nature",
        "environment",
        "green",
        "conservation",
        "earth",
      ],
      "Emergency & Crisis Relief": [
        "emergency",
        "disaster",
        "relief",
        "crisis",
        "urgent",
      ],
      "Arts & Culture": ["art", "culture", "creative", "music", "performance"],
      "Food Instability": ["food", "hunger", "nutrition", "meals", "kitchen"],
      "Housing Assistance": [
        "housing",
        "home",
        "shelter",
        "building",
        "construction",
      ],
      "Veterans & Military": [
        "veterans",
        "military",
        "service",
        "honor",
        "flag",
      ],
      "Youth & Kids": ["children", "kids", "youth", "young", "playground"],
    };

    return categoryMap[category] || ["community", "support", "help"];
  }

  /**
   * Extract relevant keywords from text
   */
  private static extractKeywords(text: string): string[] {
    // Simple keyword extraction - remove common words and get meaningful terms
    const commonWords = new Set([
      "the",
      "a",
      "an",
      "and",
      "or",
      "but",
      "in",
      "on",
      "at",
      "to",
      "for",
      "of",
      "with",
      "by",
      "is",
      "are",
      "was",
      "were",
      "be",
      "been",
      "being",
      "have",
      "has",
      "had",
      "do",
      "does",
      "did",
      "will",
      "would",
      "could",
      "should",
      "may",
      "might",
      "can",
      "this",
      "that",
      "these",
      "those",
    ]);

    return text
      .toLowerCase()
      .replace(/[^\w\s]/g, " ")
      .split(/\s+/)
      .filter((word) => word.length > 2 && !commonWords.has(word))
      .slice(0, 5);
  }

  /**
   * Select the best image from search results
   */
  private static selectBestImage(images: UnsplashImage[]): UnsplashImage {
    // Prefer images with good aspect ratio for fundraiser headers
    const landscapeImages = images.filter((img) => img.width > img.height);

    if (landscapeImages.length > 0) {
      // Sort by quality indicators (likes, downloads would be ideal but not in basic response)
      return landscapeImages[0];
    }

    return images[0];
  }

  /**
   * Fallback search for generic fundraising images
   */
  private static async fallbackSearch(
    category?: string
  ): Promise<UnsplashResponse> {
    const fallbackQueries = [
      "community support help",
      "people helping together",
      "volunteer charity",
      "fundraising donation",
      "community service",
    ];

    for (const query of fallbackQueries) {
      try {
        const response = await fetch(
          `${this.BASE_URL}/search/photos?query=${encodeURIComponent(query)}&per_page=5&orientation=landscape`,
          {
            headers: {
              Authorization: `Client-ID ${this.ACCESS_KEY}`,
              "Accept-Version": "v1",
            },
          }
        );

        if (response.ok) {
          const data: UnsplashSearchResponse = await response.json();
          if (data.results && data.results.length > 0) {
            const selectedImage = this.selectBestImage(data.results);
            await this.trackDownload(selectedImage.links.download_location);
            return { success: true, data: selectedImage };
          }
        }
      } catch (error) {
        console.error("Fallback search failed for query:", query, error);
      }
    }

    throw new Error("No suitable images found");
  }

  /**
   * Track image download for Unsplash attribution requirements
   */
  private static async trackDownload(downloadLocation: string): Promise<void> {
    try {
      await fetch(downloadLocation, {
        headers: {
          Authorization: `Client-ID ${this.ACCESS_KEY}`,
        },
      });
      console.log("📊 Download tracked for Unsplash attribution");
    } catch (error) {
      console.error("Failed to track download:", error);
      // Don't throw error as this is not critical for functionality
    }
  }

  /**
   * Get image attribution text for display
   */
  static getAttributionText(image: UnsplashImage): string {
    return `Photo by ${image.user.name} on Unsplash`;
  }

  /**
   * Get image attribution URL for linking
   */
  static getAttributionUrl(image: UnsplashImage): string {
    return `${image.links.html}?utm_source=fundraiser_app&utm_medium=referral`;
  }

  /**
   * Get optimized image URL for specific use case
   */
  static getOptimizedImageUrl(
    image: UnsplashImage,
    size: "thumb" | "small" | "regular" | "full" = "regular"
  ): string {
    return image.urls[size];
  }
}
